<script lang="ts">
  /**
   * The main stage: aspect ratio, responsive centring, and the keyboard layer.
   *
   * Keyboard bindings (ARCHITECTURE.md §6):
   *   J / K        move focus between ACTIVE lines — never settled ones, since
   *                AF1's workflow has no reason to navigate to a crossed-off item
   *   Space/Enter  launch the focus takeover on the focused task
   *   C            open quick capture
   *   Esc          step back / pause
   *
   * Bindings are ignored while a text field has focus, so typing "j" into the
   * capture drawer does not move the notebook cursor.
   */
  import type { NotebookStore, Relay } from '../storage/operations';
  import {
    ANONYMOUS_OWNER_ID,
    createNotebook,
    deleteNotebook,
    getLastNotebookId,
    getOrCreateDeviceId,
    importNotebooks,
    listNotebooks,
    renameNotebook,
    setLastNotebookId,
    storeFor,
    type Identity,
    type NotebookDraft,
    type NotebookSummary,
  } from '../storage/operations';
  import type { Account, AuthState } from '../storage/account';
  import { deleteAccount, signOut } from '../storage/account';
  import { dropLocalNotebook } from '../storage/db';
  import { readTheme, resolveTheme, writeTheme, type Theme } from '../storage/theme';
  import type {
    AutofocusSession,
    Notebook,
    RenderModel,
  } from '../types/generated';
  import { emptyProjection, tasksOnPage } from '../storage/fold';
  import { splitTitleLink } from '../utils/taskStats';
  import { createInactivityTracker } from '../services/inactivityTracker';
  import Icon from './Icon.svelte';
  import LegalFooter from './LegalFooter.svelte';
  import X from 'lucide/dist/esm/icons/x.mjs';
  import BrowseModeView from './BrowseModeView.svelte';
  import DebugLogView from './DebugLogView.svelte';
  import DeleteNotebookModal from './DeleteNotebookModal.svelte';
  import DeleteAccountModal from './DeleteAccountModal.svelte';
  import AdminPanel from './AdminPanel.svelte';
  import FocusTakeoverModal from './FocusTakeoverModal.svelte';
  import GraveyardView from './GraveyardView.svelte';
  import InFlightQuickCapture from './InFlightQuickCapture.svelte';
  import LandingHub from './LandingHub.svelte';
  import NotebookCreateModal from './NotebookCreateModal.svelte';
  import NotebookListView from './NotebookListView.svelte';
  import NextPageMargin from './NextPageMargin.svelte';
  import PageCard from './PageCard.svelte';
  import PageFooter from './PageFooter.svelte';
  import ReentryBottomSheet from './ReentryBottomSheet.svelte';
  import SettingsView from './SettingsView.svelte';
  import SignInView from './SignInView.svelte';
  import UndoSnackbar from './UndoSnackbar.svelte';
  import { parseRoute, buildRoute } from '../routes';
  import '../styles/paper.css';

  interface Props {
    /**
     * An already-open notebook: the container starts on it (the Step 5/6 test
     * shape, and every device that reopens a store it holds). Absent — the
     * real app's first run — the container mounts at the LIST (D1).
     */
    store?: NotebookStore | null;
    /** Transport injection for tests; production falls back to `fetch`. */
    relay?: Relay;
    /**
     * The boot gate's verdict (R4.9 §5): the relay's mode, who is signed in, and
     * whether sign-in exists at all. Absent — every mount that has no gate in
     * front of it, which is the test shape and the whole pre-account world — the
     * container behaves exactly as it did before this: anonymous, and with no
     * sign-in surface of any kind.
     */
    auth?: AuthState | null;
    /**
     * The owner this browser is acting as (R7), from the same gate. Every store
     * the container composes is addressed to it: without it, a signed-in user's
     * notebooks would be opened under the anonymous owner, whose ops the relay's
     * gate refuses as cross-owner. Defaults to the anonymous owner, which is what
     * a mount with no gate means.
     */
    owner_id?: string;
    /**
     * This browser's device id, as the gate resolved it (R7). Optional: the two
     * ids agree by construction (`getOrCreateDeviceId` lives in `localStorage`,
     * §5), so a mount that omits it mints the same one on demand.
     */
    device_id?: string | null;
  }

  let {
    store: initialStore = null,
    relay,
    auth = null,
    owner_id = ANONYMOUS_OWNER_ID,
    device_id = null,
  }: Props = $props();

  /**
   * The per-notebook identity this container composes (R7): the acting owner
   * from the gate plus this browser's device. The gate hands the device id over
   * because it already read it; a mount without one mints it here.
   */
  async function identityFor(notebookId: string): Promise<Identity> {
    return {
      owner_id,
      device_id: device_id ?? (await getOrCreateDeviceId()),
      notebook_id: notebookId,
    };
  }

  // ── the notebook list (STEP_7_SPEC.md D1, D7) ───────────────────────────────

  type View = 'list' | 'notebook' | 'hub' | 'browse' | 'graveyard' | 'debuglog' | 'settings' | 'admin' | 'signin' | 'notfound';

  /**
   * R4.9 §5/§7 (Task 11): this browser signed out from the account panel while
   * the app was up.
   *
   * The container owns the transition because the RELAY's answer arrives one
   * boot later: `auth` is the verdict the gate resolved, and a prop cannot be
   * rewritten from here. Signing out therefore retires it locally — the browser
   * is nobody again, which for a relay with accounts means the sign-in view and
   * nothing else — and the next boot asks `/api/auth/me` again, which is the
   * authority.
   */
  let signedOut = $state(false);

  /** The signed-in account, or null — nobody, an unclaimed relay, or signed out. */
  const signedIn: Account | null = $derived(signedOut ? null : (auth?.user ?? null));

  /**
   * The gate's verdict as THIS page reads it: the resolved one, or — after a
   * sign-out — account mode with no live login, which is the state the relay's
   * own `401` produces (§5). Everything that decides what is reachable reads
   * this, so the sign-in view the sign-out lands on is the same gate, not a
   * second, weaker one.
   */
  const gate: AuthState | null = $derived(
    signedOut && auth !== null
      ? { mode: 'account', user: null, signin_available: auth.signin_available }
      : auth,
  );

  /**
   * Does the gate put the sign-in view up as the whole app?
   *
   * Account mode with nobody signed in, and sign-in available: the relay refuses
   * every request this browser could make, so there is nothing to show but the
   * way in. `signin_available` is the D2 guard — with Google unconfigured there
   * is no affordance to show, and a relay in this state refuses to start at all
   * (`validate_startup`), so the miss falls through to the ordinary behaviour
   * instead of inventing a dead end.
   */
  function signinRequiredFor(state: AuthState | null): boolean {
    return state !== null && state.mode === 'account' && state.user === null && state.signin_available;
  }

  /**
   * The live gate verdict. A prop, so it is reactive: a host that resolves the
   * account again re-renders this container's gate branches with no extra wiring.
   */
  const signinRequired = $derived(signinRequiredFor(gate));

  /**
   * An unclaimed relay that COULD be claimed: the app is the app, plus the one
   * entry point that starts the first signup (§6, §7).
   */
  const signinOffered = $derived(
    gate !== null && gate.mode === 'anonymous' && gate.user === null && gate.signin_available,
  );

  /** The sign-in view's address, built rather than spelled (§7). */
  const SIGNIN_PATH = buildRoute({ kind: 'signin' });

  /**
   * The live store. The container — not main.ts, not the list — owns store
   * lifecycle: `openNotebook` builds or reuses one, `closeNotebook` leaves it
   * mounted so its outbox keeps draining in the background (D1).
   */
  // svelte-ignore state_referenced_locally
  let activeStore = $state<NotebookStore | null>(initialStore);

  /**
   * The view state (D1). No router. A provided store starts on the notebook —
   * or on the hub when that store's persisted cell is already TimedOut (the
   * reload-after-the-mark flow, Step 6 D1).
   *
   * The gate OUTRANKS all of that (§5): when the relay has accounts and this
   * browser has no live login, the sign-in view is the app, and a `store` prop
   * cannot talk it out of that — there is nothing behind the login to show.
   * It reads `gate`, not the `auth` prop, so the seed and every later correction
   * (`applyPath`, the effect below) ask the same question: `signedOut` is false
   * before the first render, so the two are the same verdict here.
   */
  // svelte-ignore state_referenced_locally
  let view = $state<View>(
    signinRequiredFor(gate)
      ? 'signin'
      : initialStore?.session?.status === 'TimedOut'
        ? 'hub'
        : initialStore
          ? 'notebook'
          : 'list',
  );

  /** The list payload, refreshed on mount and on every return from a notebook. */
  let notebooks = $state<NotebookSummary[]>([]);

  const activeTag = $derived(
    view === 'list' || view === 'notfound' || view === 'signin'
      ? null
      : notebooks.find((n) => n.notebook_id === activeStore?.identity.notebook_id)?.tag ?? null,
  );

  /** The creation modal (D8): open from the list's affordance or an
   *  unknown-tag capture (Task 6). The summary, when editing (D3). */
  let createModalOpen = $state(false);
  let editingNotebook = $state<NotebookSummary | null>(null);
  /** From the capture that opened the modal (§D4). `undefined` — not `''` —
   *  when there was no capture, which is what keeps the first-task field off a
   *  notebook created from the list's own affordance. A bare `@tag` with no
   *  text sets it to `''`, and still deserves the field. */
  let capturedTag = $state('');
  let capturedFirstTask = $state<string | undefined>(undefined);

  /** Per-notebook store cache — capture routing reuses a warm store (D10). */
  const stores = new Map<string, NotebookStore>();

  /** R2.1, Task 2: the not-found view's URL — verbatim, never rewritten. */
  let notFoundPath = $state('');
  /** R2.1, Task 2: the tag named in a bad `/notebook/{tag}` URL; null otherwise. */
  let notFoundTag = $state<string | null>(null);
  /** R2.1: flips true once boot resolution finishes. The mirror effect (Task 3)
   *  reads it to choose `replaceState` (under boot, including popstate) vs
   *  `pushState` (live view transitions). Test passes a `store` prop; boot is
   *  skipped for those mounts, so `booted` starts true. */
  // svelte-ignore state_referenced_locally
  let booted = $state(initialStore !== null);
  /** R2.1: a per-popstate counter; `openNotebook` checks it to drop stale
   *  writes when a back-press lands mid-await. Incremented by Task 3. */
  let navEpoch = 0;

  async function refreshList(): Promise<void> {
    const result = await listNotebooks(relay);
    notebooks = result.notebooks;
  }

  /**
   * R2.1, Task 2: the shared resolver — boot reads it once, and Task 3's
   * `popstate` listener reads it on every back/forward. Imports `parseRoute`
   * from `routes.ts` and routes through `openNotebook` so the precedence
   * outcomes (checkpoint → hub → fast-path) are the same shape every path in.
   *
   * `rule 2` (only override `view` with the sub-view when precedence landed
   * on the notebook rather than the hub) is the precedence-outranks-deep-link
   * ruling — already a property of the notebook entry, surfaced at boot/pop.
   *
   * The `notfound.tag ?? null` line guards against a lie (spec §4.1): `first`
   * resolves, so the tag copy must not render — null out so the markup picks
   * the generic copy.
   */
  async function applyPath(pathname: string): Promise<void> {
    // The gate outranks the URL in BOTH directions (§5): while it stands, no
    // path — a Back press out of the view included — walks back into the app,
    // and the URL is corrected to the one place that is reachable. Nothing else
    // is addressable when the relay refuses every request this browser makes.
    if (signinRequiredFor(gate)) {
      view = 'signin';
      if (location.pathname !== SIGNIN_PATH) history.replaceState(null, '', SIGNIN_PATH);
      return;
    }
    const route = parseRoute(pathname);
    if (route.kind === 'list') {
      if (activeStore) closeNotebook();
      else view = 'list';
      return;
    }
    // R4.9 §7: the sign-in view is addressable like the settings page, and it
    // outranks nothing — it is simply where the URL says to go. When the gate
    // already put it up, this is the same answer twice.
    //
    // But only where the view may be SHOWN: `signinOffered` is the anonymous
    // half of §7's rule, and the account half is the gate branch above. Neither a
    // relay with Google unconfigured (D2: no sign-in surface at all) nor a
    // browser that is already signed in can be at `/signin`, however the path got
    // there — so the front door answers and the URL is corrected in place, the
    // same correction the gate branch makes, for the same reason: the view is
    // decided by the relay's mode, not by the path.
    if (route.kind === 'signin') {
      if (!signinOffered) {
        view = 'list';
        if (location.pathname !== '/') history.replaceState(null, '', '/');
        return;
      }
      view = 'signin';
      return;
    }
    if (route.kind === 'notebook') {
      const summary = notebooks.find((n) => n.tag === route.tag);
      if (!summary) {
        view = 'notfound';
        notFoundPath = pathname;
        notFoundTag = route.tag;
        return;
      }
      await openNotebook(summary.notebook_id);
      // Rule 2: only when the precedence outcome IS the notebook does the
      // sub-view override apply. A checkpoint/hub outcome keeps the URL's
      // intent off the view — the mirror in Task 3 will then REPLACE the
      // path with the notebook's bare address.
      if (route.sub !== undefined && view === 'notebook') {
        if (route.sub === 'browse') openBrowse();
        else if (route.sub === 'graveyard') openGraveyard();
        else if (route.sub === 'debuglog') openDebugLog();
      }
      return;
    }
    // R3.2 §3: checkpoint → hub precedence wins over /settings — the same D2
    // rule as deep links. Only when precedence lands on the notebook does the
    // settings view apply; a hub outcome (or a deleted-elsewhere eviction to
    // the list) keeps its own view.
    if (route.kind === 'settings') {
      const last = await getLastNotebookId();
      if (last && notebooks.some((n) => n.notebook_id === last)) {
        await openNotebook(last);
        if (view !== 'notebook') return;
      }
      view = 'settings';
      return;
    }
    // R4.9 §7 (Task 11): the account page. Addressable like settings, and with
    // no checkpoint→hub precedence of its own: it shows who this browser is, not
    // a notebook, so there is nothing for the precedence to outrank. What it
    // DOES need is an ADMIN to show: the page is where sign-out and the admin
    // half live, and a member has neither there — their settings page carries
    // sign-out instead. A path here with nobody signed in, or with somebody who
    // is not the admin, is not a place: the front door (nobody) or settings
    // (a member) answers, and the URL is corrected to it.
    if (route.kind === 'admin') {
      if (signedIn === null) {
        view = 'list';
        if (location.pathname !== '/') history.replaceState(null, '', '/');
        return;
      }
      if (!signedIn.is_admin) {
        view = 'settings';
        if (location.pathname !== '/settings') history.replaceState(null, '', '/settings');
        return;
      }
      view = 'admin';
      return;
    }
    // route.kind === 'notfound'
    view = 'notfound';
    notFoundPath = route.path;
    // Lie guard: if the path's tag actually resolves, the "no notebook with
    // the tag X" line would lie. The bad sub is bad because of the sub, not
    // because of the tag.
    notFoundTag = route.tag !== undefined && notebooks.some((n) => n.tag === route.tag)
      ? null
      : (route.tag ?? null);
  }

  /**
   * IDEAS 10: imports a whole-notebook bundle through the relay. Authors
   * nothing durable locally (no outbox op) — it is a relay bulk write, and
   * the refresh reads the relay's truth. A failure surfaces as its own
   * toast: the op-rejection notices belong to the outbox, this belongs to
   * the transfer surface.
   */
  let importError = $state<string | null>(null);
  /** The notebook awaiting delete confirmation, or null when no modal is up. */
  let deleteTarget = $state<NotebookSummary | null>(null);
  /** The one-line notice after evicting a notebook deleted elsewhere (R3.2 §2). */
  let deletedNotice = $state<string | null>(null);
  async function importNotebookFile(file: File): Promise<void> {
    try {
      await importNotebooks(await file.text());
      importError = null;
      await refreshList();
    } catch (e) {
      importError = e instanceof Error ? e.message : String(e);
    }
  }

  $effect(() => {
    // The gate first (§5): account mode with no live login leaves nothing else
    // reachable, and the boot resolution below would be a round trip whose only
    // possible answer is 401.
    if (signinRequired) {
      view = 'signin';
      return;
    }
    void refreshList().then(() => {
      // R2.1: the boot resolver reads the URL only on the first mount — the
      // test shape (a `store` prop) starts on its own view and skips URL
      // resolution, so its `booted` was seeded true.
      if (initialStore) return;
      void applyPath(location.pathname).then(() => {
        booted = true;
      });
    });
  });

  /**
   * R2.1, Task 3: the URL mirror. The view union is finite; ask `buildRoute`
   * for the canonical path of each one. The notfound view's URL is whatever
   * the user typed and is preserved verbatim (`notFoundPath`).
   *
   * Skip when `activeTag` is null AND the view is not notfound — the
   * pre-refresh case (a fresh mount would push `/` from a stale read), and
   * any notebook-family view without a tag (no key to URL by).
   *
   * The LIST is exempt from that guard once booted: `[Close]` and
   * `[Back to list]` land here with a deliberately tag-less view and must
   * still push `/`, while a fresh mount must not clobber
   * `/notebook/{tag}` before `applyPath` has resolved it.
   *
   * `booted` chooses the action: live view transitions `pushState` (so the
   * back button returns to the prior URL); boot-induced corrections
   * `replaceState` (history should not show two entries for the same
   * mount-time correction).
   *
   * Pushed only when `target !== location.pathname` — when they are equal,
   * the mirror is a no-op, the property tests 2, 3, 4, 6 and 7 rely on.
   */
  $effect(() => {
    let target: string;
    if (view === 'list') {
      if (!booted) return;
      target = '/';
    } else if (view === 'signin') {
      target = SIGNIN_PATH;
    } else if (view === 'settings') {
      target = buildRoute({ kind: 'settings' });
    } else if (view === 'admin') {
      target = buildRoute({ kind: 'admin' });
    } else if (view === 'notfound') {
      target = notFoundPath;
    } else if (activeTag !== null) {
      const sub =
        view === 'browse' || view === 'graveyard' || view === 'debuglog'
          ? view
          : undefined;
      target = buildRoute({ kind: 'notebook', tag: activeTag, sub });
    } else {
      return;
    }
    if (target === location.pathname) return;
    if (booted) {
      history.pushState(null, '', target);
    } else {
      history.replaceState(null, '', target);
    }
  });

  /**
   * R2.1, Task 4: a tab close mid-task must confirm. Two states warrant a
   * prompt the browser will surface: a takeover open (`takeoverTaskId !== null`)
   * and a drawer's unsaved text. The `captureOpen &&` matters — an Esc from
   * the window handler only clears `captureOpen`, leaving `captureText` stale
   * until the next append; warning on stale text is the false positive the
   * guard exists to avoid. `$state` reads inside the listener are live-proxy
   * reads — the closure doesn't cache them across calls.
   *
   * Shared with the back button below: the SAME two states, so leaving by
   * either route asks the same question.
   */
  function hasUnsavedWork(): boolean {
    return takeoverTaskId !== null || (captureOpen && captureText !== '');
  }

  const UNSAVED_WORK_MESSAGE = 'You have unsaved work here. Leave anyway?';

  $effect(() => {
    const handler = (event: Event) => {
      if (hasUnsavedWork()) {
        event.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  });

  /**
   * R2.1, Task 3: the popstate listener — `navEpoch += 1` invalidates any
   * `openNotebook` already in flight (its three `view =` writes check the
   * epoch and return when stale). Then routes through the shared resolver
   * with `location.pathname` as the input, exactly what boot reads.
   *
   * The same unsaved-work guard as `beforeunload`, because Back is the other
   * way out of a task in flight and `beforeunload` never fires for it (no
   * document is unloading — only the history cursor moved). Declining puts
   * the cursor back with `go(1)`, whose own popstate is swallowed by the
   * flag; `go(1)` always has a forward entry, since a back-press is what put
   * us here.
   *
   * Svelte's effect cleanup runs on unmount; the listener is replaced if
   * the effect re-runs (it doesn't read any reactive value, so it
   * registers once).
   */
  $effect(() => {
    let declined = false;
    const handler = () => {
      if (declined) {
        declined = false;
        return;
      }
      if (hasUnsavedWork() && !window.confirm(UNSAVED_WORK_MESSAGE)) {
        declined = true;
        history.go(1);
        return;
      }
      navEpoch += 1;
      void applyPath(location.pathname);
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  });

  /**
   * Opens a notebook: reuse a warm store or bootstrap one over the relay, then
   * evaluate D2's precedence — CHECKPOINT → HUB → FAST-PATH, always in that
   * order.
   *
   * 1. The session cell's `current_task_id` names an unresolved task → the hub
   *    with a live checkpoint banner. A fast-path that skipped this would
   *    leave a task nothing resolves until the next device acts (Review
   *    Focus #5).
   * 2. The fast-path: this notebook is `meta.lastNotebookId` AND its wake
   *    check would not fire — Step 6's own 4-hour window, the same seed and
   *    the same max, no second clock. It lands at the session cell's page and
   *    writes nothing (§4).
   * 3. Otherwise the hub is the front door: a foreign notebook, a wake-window
   *    expiry — its Resume is live immediately when there is no checkpoint.
   *
   * Catches up over `/log` BEFORE evaluating (§8.2) — the cell and the
   * projection the checkpoint reads must be the newest prefix.
   */
  async function openNotebook(id: string): Promise<void> {
    // R2.1, Task 3: capture the nav epoch the user pressed at. Each of the
    // three `view =` writes below drops the navigation when a back-press
    // arrived mid-await. The store warming above is intentional: caches are
    // keyed by id, and a stale open completes its bookkeeping rather than
    // throwing away fetched state.
    const epoch = navEpoch;
    let target = stores.get(id);
    if (!target) {
      target = await storeFor(await identityFor(id));
      if (relay) target.setTransport(relay);
      stores.set(id, target);
    }
    const caughtUp = await target.catchUp(relay);
    // R3.2 §2, other device with the notebook open: the /log 404 is the
    // signal — evict to the list with the one-line notice, then drop local
    // rows. Never re-author a CreateNotebook for a dropped id.
    if (caughtUp.deleted) {
      stores.delete(id);
      if (activeStore?.identity.notebook_id === id) activeStore = null;
      await dropLocalNotebook(id);
      await refreshList();
      if (epoch !== navEpoch) return;
      view = 'list';
      deletedNotice = 'Notebook deleted elsewhere';
      return;
    }
    await target.loadSession();
    // The open IS the launch: drain the outbox after catching up (§8.2's
    // order), or offline-authored work waits until the user authors ANOTHER
    // op in that notebook — the Review Focus #4 flow stalls with it.
    void target.flush();
    activeStore = target;

    // 1. CHECKPOINT — an unresolved task wins over everything below.
    const cell = target.session;
    const task = cell?.current_task_id
      ? target.model.predicted.tasks[cell.current_task_id]
      : null;
    if (task?.state === 'Active') {
      if (epoch !== navEpoch) return;
      view = 'hub';
      return;
    }

    // 2. FAST-PATH — last-opened on this device, inside the wake window.
    const last = await getLastNotebookId();
    if (last === id && !(await target.hasTimedOut())) {
      if (epoch !== navEpoch) return;
      currentPageNumber = cell?.current_page_number ?? 1;
      focusedTaskId = null;
      view = 'notebook';
      return;
    }

    // 3. HUB — the front door.
    if (epoch !== navEpoch) return;
    view = 'hub';
  }

  /**
   * `[Close]`: the notebook becomes this device's last-opened one (D2's
   * fast-path key) and the list returns — refreshed, so a notebook committed
   * milliseconds ago is already counted.
   */
  function closeNotebook(): void {
    const target = activeStore;
    if (!target) return;
    void setLastNotebookId(target.identity.notebook_id);
    view = 'list';
    void refreshList();
  }

  /**
   * Creates a notebook from the modal's draft (§D6): one outbox, CreateNotebook
   * then AppendTask. From the LIST the user lands on the new notebook; from a
   * capture (Task 6) the user stays wherever they were.
   */
  async function commitCreate(draft: NotebookDraft): Promise<void> {
    const created = await createNotebook(draft, relay);
    if (relay) created.setTransport(relay);
    stores.set(created.identity.notebook_id, created);
    createModalOpen = false;
    capturedTag = '';
    capturedFirstTask = undefined;
    if (view !== 'list') return; // capture path: the user stays put
    activeStore = created;
    view = 'notebook';
    currentPageNumber = 1;
    focusedTaskId = null;
  }

  /**
   * Edits a notebook from the list (§D3): the text fields ride ONE
   * RenameNotebook. The notebook need not be open — its store is warmed on
   * demand so the rename's base_seq is correct. A cleared tag means unchanged.
   */
  async function commitEdit(id: string, draft: NotebookDraft): Promise<void> {
    let target = stores.get(id);
    if (!target) {
      target = await storeFor(await identityFor(id));
      if (relay) target.setTransport(relay);
      await target.catchUp(relay);
      stores.set(id, target);
    }
    await renameNotebook(target, {
      name: draft.name,
      tag: draft.tag === '' ? undefined : draft.tag,
      description: draft.description,
    });
    editingNotebook = null;
    // The row updates when the drain lands — the list reads the relay's truth,
    // and the click itself never waits on the network.
    void target.flush().then(() => refreshList());
  }

  function openCreate(): void {
    editingNotebook = null;
    capturedTag = '';
    capturedFirstTask = undefined;
    createModalOpen = true;
  }

  function openEdit(id: string): void {
    editingNotebook = notebooks.find((n) => n.notebook_id === id) ?? null;
    capturedTag = '';
    capturedFirstTask = undefined;
    createModalOpen = true;
  }

  /** Closes the modal, authoring nothing — Esc and Cancel both land here. */
  function cancelModal(): void {
    createModalOpen = false;
    editingNotebook = null;
  }

  /** Task 6 wires the capture's unknown-tag handoff into these fields. */
  function capturedModalOpen(): void {
    editingNotebook = null;
    createModalOpen = true;
  }

  /**
   * The capture host's routing callback (§D4): a KNOWN tag appends to that
   * notebook's store — bootstrapped on demand, caught up so the append's
   * `base_seq` and fold are correct — and an UNKNOWN one opens the creation
   * modal pre-filled with the capture. A bare `@tag` with no title and no
   * such notebook commits the notebook, not a task.
   *
   * The route table is the LIST (Review Focus #1) — relay refresh plus the
   * meta fallback. A miss re-checks against a fresh refresh before falling
   * back to the modal: the route table can be seconds stale, and a capture
   * fired before the mount's refresh has landed must still route.
   */
  async function routeCapture(tag: string, title: string): Promise<void> {
    let summary = notebooks.find((n) => n.tag === tag);
    if (!summary) {
      await refreshList();
      summary = notebooks.find((n) => n.tag === tag);
    }
    if (!summary) {
      capturedTag = tag;
      capturedFirstTask = title;
      capturedModalOpen();
      return;
    }
    if (title.trim() === '') return; // a bare tag on a known notebook: nothing to append
    await appendToNotebook(summary.notebook_id, title);
    // A cross-notebook route names the notebook; a self-tag lands on this
    // notebook's Final page, which is toast-worthy only when it is off screen.
    if (summary.notebook_id !== store.identity.notebook_id) pushToast(summary.name, title);
    else noteOffscreenLanding(title);
  }

  async function appendToNotebook(id: string, title: string): Promise<void> {
    let target = stores.get(id);
    if (!target) {
      target = await storeFor(await identityFor(id));
      if (relay) target.setTransport(relay);
      // Catch up over /log BEFORE authoring (§8.2): the append's base_seq and
      // the fold both depend on the newest prefix.
      await target.catchUp(relay);
      await target.loadSession();
      stores.set(id, target);
    }
    const taskId = await target.appendTask(title, null);
    // R4.6: the tag-routed append lands the memory too — with the notebook it
    // actually went to, which may not be the one being looked at.
    lastCaptured = { taskId, title, notebookId: target.identity.notebook_id };
  }

  /**
   * The page the user is looking at. The Final page is the default.
   */
  let currentPageNumber = $state<number | null>(null);
  let lastFooterSkippedPages = $state<number[]>([]);
  let focusedTaskId = $state<string | null>(null);
  let captureOpen = $state(false);
  /**
   * R2.1, Task 4: the drawer's binding target. The component is bindable, the
   * host owns the text — mirroring the takeover, which keeps the field local.
   * Stale-on-close: an Esc here only sets `captureOpen = false` and leaves the
   * text set, so the `beforeunload` guard keys off `captureOpen && captureText`
   * rather than on text alone (one false-positive close would teach users to
   * dismiss the warning).
   */
  let captureText = $state('');
  /**
   * R4.6: the last capture that LANDED — an append or a reword — with the
   * notebook it lives in. `e` re-opens the capture prefilled as an edit, but
   * only within reach: the reword goes through the drawer's store, so a task
   * routed to another `@tag` is out of reach from this notebook.
   */
  let lastCaptured = $state<{ taskId: string; title: string; notebookId: string } | null>(null);
  /** The edit in flight: `e` moves the memory here, the field's close clears it. */
  let editTarget = $state<{ taskId: string; title: string } | null>(null);
  let relayoutIds = $state<Set<string>>(new Set());

  /** The task in flight, or `null` when the page is what is being looked at. */
  let takeoverTaskId = $state<string | null>(null);
  /**
   * The stage element, the page's focus target (idea 14): when the hub exits,
   * focus returns here rather than falling to `<body>`.
   */
  let stageEl = $state<HTMLElement | null>(null);
  /** The re-entry sheet, which opens from the takeover rather than replacing it. */
  let reentryOpen = $state(false);
  /**
   * The re-entry sheet's SECOND mount: the banner path (D6). The takeover and
   * the banner never have the sheet open at once — the takeover renders only
   * in the notebook view (idea 14), so it is gone when the banner is up.
   */
  let bannerReentryOpen = $state(false);
  /**
   * Server verdicts not yet shown (§0.3).
   *
   * Drained from the store on each emit so a notice is rendered once rather than
   * re-rendered on every emit. It lives here because Review Focus #1 rules that
   * the takeover closes on *author* — so a `TASK_ALREADY_SETTLED` verdict
   * necessarily arrives after the takeover has gone, and a notice rendered only
   * inside it would be unreachable.
   */
  let rejections = $state<Array<{ op_id: string; reason: string }>>([]);

  /**
   * The capture-saved notice (IDEAS 11): the destination notebook's name when a
   * `@tag`-routed capture landed there. Plain local state — the trigger is the
   * host's own routing decision, not a store verdict, so there is nothing to
   * drain. ONE state, ONE render site — the bottom-right toast, taken over
   * from the takeover (it used to banner inside the takeover and shove the
   * task title down). Only `@tag` routes set it: a plain append to the open
   * notebook is self-evidently placed, and a bare `@tag` appends nothing at
   * all.
   */
  /**
   * The capture-saved toasts (IDEAS 11, amended twice): they pile bottom-right,
   * each expiring on its own after 8 seconds — the undo snackbar's rhythm.
   * Expired or dismissed, the entry leaves the pile; dismissal is per toast.
   */
  const TOAST_TTL_MS = 8_000;
  /** How long the leaving toast's fade-down plays before it leaves the pile. */
  const TOAST_EXIT_MS = 220;
  let captureToasts = $state<Array<{ id: number; label: string; title: string; leaving: boolean }>>([]);
  let toastSeq = 0;

  /** The first few words of a task title, for the toast's annotation. */
  function titleSnippet(title: string): string {
    const trimmed = title.trim();
    const words = trimmed.split(/\s+/);
    return words.length > 4 ? `${words.slice(0, 4).join(' ')}…` : trimmed;
  }

  function pushToast(label: string, title: string): void {
    const id = ++toastSeq;
    captureToasts = [...captureToasts, { id, label, title: titleSnippet(title), leaving: false }];
    setTimeout(() => void dismissToast(id), TOAST_TTL_MS);
  }

  /** Marks the toast leaving — its fade-down plays — then drops it from the pile. */
  function dismissToast(id: number): void {
    captureToasts = captureToasts.map((t) => (t.id === id ? { ...t, leaving: true } : t));
    setTimeout(() => {
      captureToasts = captureToasts.filter((t) => t.id !== id);
    }, TOAST_EXIT_MS);
  }

  /**
   * The capture-saved toast for a landing in THIS notebook (the amended rule):
   * a capture always files to the Final page, so the toast fires only when
   * that is not the page on screen — the task went somewhere the user is not
   * looking. A cross-notebook route names the notebook instead (routeCapture).
   */
  function noteOffscreenLanding(title: string): void {
    const writing = pages.find((p) => p.status === 'Writing');
    if (writing && writing.page_number !== (currentPageNumber ?? firstActivePage())) {
      pushToast('Final page', title);
    }
  }

  /**
   * R5.11 (user ruling 2026-10-16): re-entry files its children on the Final
   * page exactly like a capture, so it pops the same offscreen toast when
   * that page is not the one on screen. The first child's title is the
   * annotation — the wording the user just committed.
   */
  function noteReentryLanding(titles: string[]): void {
    const title = titles[0];
    if (title === undefined) return;
    noteOffscreenLanding(title);
  }

  /**
   * The capture field, so the drawer can take focus when it opens.
   *
   * `InFlightQuickCapture` focuses its own input on mount — the drawer used to
   * open with focus still on `<body>`, and because `onKeydown` only defers to a
   * field when the *event target* is one, every following key was read as a
   * notebook command: `j`/`k` moved the cursor, `c` closed the drawer, and
   * `Space`/`Enter` finished a task.
   *
   * What the container still owns is `captureOpen`, because `Esc` closes the
   * drawer from the window handler as well as from the field's own.
   */
  /**
   * The store's render-relevant state, held locally.
   *
   * `store.model` and `store.session` are plain getters, which Svelte cannot
   * track — reading them from `$derived` would compute once and never
   * recompute, and the view would silently stop reflecting the store. Snapshotting
   * them in `$state` on each emit makes every derived below genuinely reactive.
   */
  /**
   * Seeded from the active store's current values and then kept current by the
   * subscription below. A store passed as a prop has its identity fixed for the
   * life of this component, so reading it once here is the correct seed — the
   * `svelte-ignore`s below say exactly that, since Svelte would otherwise read
   * a one-time read as a mistake.
   */
  // svelte-ignore state_referenced_locally
  let model = $state<RenderModel>(
    initialStore?.model ?? { committed: emptyProjection(), predicted: emptyProjection() },
  );
  // svelte-ignore state_referenced_locally
  let session = $state<AutofocusSession | null>(initialStore?.session ?? null);

  /**
   * R19 (spec §5): the relay refused a drain with a `401` while the app is up, so
   * the login the app booted with is gone and the outbox is paused. The store
   * owns the flag (`authRequired`) and raises it on its own emit; this is the one
   * place it is read, and the surface below is what reads it.
   *
   * Snapshot in `$state` for the same reason `model`/`session` are: the store's
   * getter is not reactive, so reading it from a derived would compute once and
   * never recompute.
   */
  // svelte-ignore state_referenced_locally
  let authExpired = $state(initialStore?.authRequired ?? false);

  /**
   * R5.15 (user ruling 2026-10-16): the same snapshot idiom, for the store's
   * `online` flag — the relay unreachable (a failed drain or catch-up) or the
   * browser's own offline event. The notice below is its one render site.
   */
  // svelte-ignore state_referenced_locally
  let offline = $state(initialStore?.online === false);

  /**
   * Theme preference: auto (follows the OS), light, or dark. Read from
   * `localStorage` at mount and persisted back on every change; applied as
   * `data-theme` on `html` below. Dark is the ruled chalkboard surface.
   */
  let theme = $state(readTheme());

  $effect(() => {
    const resolved = resolveTheme(theme);
    document.documentElement.dataset.theme = resolved;
    document.body.dataset.theme = resolved;
    // Re-resolve `auto` when the OS preference flips.
    if (typeof matchMedia === 'undefined') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (theme === 'auto') {
        const next = resolveTheme('auto');
        document.documentElement.dataset.theme = next;
        document.body.dataset.theme = next;
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  });

  function setTheme(next: Theme): void {
    writeTheme(next);
    theme = next;
  }

  $effect(() => {
    const target = activeStore;
    if (!target) return;
    model = target.model;
    session = target.session;
    authExpired = target.authRequired;
    offline = !target.online;
    return target.subscribe(() => {
      model = target.model;
      session = target.session;
      authExpired = target.authRequired;
      offline = !target.online;
      // Drain on the emit, not on every read: a notice bound to a growing list
      // would be re-rendered each time anything changed.
      const drained = target.drainRejections();
      if (drained.length > 0) rejections = drained;
    });
  });

  // ── the timeout checkpoint (STEP_6_SPEC.md D1, D8) ──────────────────────────

  // D8: the tracker is created and torn down here. It exposes no events — the
  // container learns of a timeout by observing session.status (the derived
  // view above), the same signal everything else reads.
  $effect(() => {
    const target = activeStore;
    if (!target) return;
    const tracker = createInactivityTracker(target);
    void tracker.start();
    return () => tracker.stop();
  });

  // Render the PREDICTED half: pending rows must be visible immediately, at
  // their predicted line, with a badge (ARCHITECTURE.md §4.1).
  const projection = $derived(model.predicted);

  const pendingIds = $derived(
    new Set(
      Object.entries(model.predicted.tasks)
        .filter(([id]) => !(id in model.committed.tasks))
        .map(([id]) => id),
    ),
  );

  const pages = $derived(
    Object.values(projection.pages).sort((a, b) => a.page_number - b.page_number),
  );

  const capacity = $derived(projection.notebook.page_capacity);

  /** The first active page, or page 1 (AF1: a sweep starts at the first active). */
  function firstActivePage(): number {
    const active = Object.values(projection.tasks)
      .filter((t) => t.state === 'Active')
      .map((t) => projection.pages[t.page_id]?.page_number ?? 1);
    return active.length > 0 ? Math.min(...active) : 1;
  }

  function footerNextPage(): number {
    const writingPage = pages.find((candidate) => candidate.status === 'Writing');
    if (page?.status === 'Writing') {
      const hasActiveTasks = Object.values(projection.tasks).some(
        (task) => task.state === 'Active',
      );
      return hasActiveTasks
        ? firstActivePage()
        : writingPage?.page_number ?? page.page_number;
    }

    return (
      laterActivePage?.page_number ??
      writingPage?.page_number ??
      page?.page_number ??
      firstActivePage()
    );
  }

  function advanceFromFooter(): void {
    if (!page) return;
    const target = footerNextPage();
    void goToPage(
      target,
      skippedPages.map((skipped) => skipped.page_number),
    );
  }

  const page = $derived(
    pages.find((p) => p.page_number === (currentPageNumber ?? firstActivePage())) ??
      pages[0],
  );

  /**
   * The view is STICKY (IDEAS.md idea 7): the first-active page is the ENTRY
   * seed, never a live fallback. The `?? firstActivePage()` above re-evaluates
   * on every store emit — including every append — so with no explicit page
   * (the store-prop mount; hub exits and the fast-path all seed one) an
   * append that lands an Active task on the writing page YANKED the view to
   * that page the instant it rendered. Pinning once at entry keeps every
   * later append, seal and settle exactly where the user left the view;
   * only an explicit navigation (`goToPage`, the hub exits) moves it.
   */
  $effect(() => {
    if (view === 'notebook' && currentPageNumber === null) {
      currentPageNumber = firstActivePage();
    }
  });

  /**
   * R3.1: the page-turn flip. A diff of the SHOWN page number, not a hook in
   * each writer — every turn (goToPage, the hub exits, a deep link, the wrap
   * back to the start) changes the number exactly once. Entry re-seeds, so
   * landing on a page never flips; only a change after one was shown does.
   * The flip is a property of the destination card itself: a same-card
   * rotation. A slide that renders the adjacent page's tasks mid-turn would
   * violate the NextPageMargin ruling — that margin is the adjacent page's
   * only content — and the one-card invariant is what keeps that true.
   */
  let shownPage = $state<number | null>(null);
  let turn = $state<'forward' | 'back' | null>(null);
  let turnTimer: ReturnType<typeof setTimeout> | undefined;

  $effect(() => {
    if (view !== 'notebook') {
      shownPage = null;
      turn = null;
      return;
    }
    const n = page?.page_number ?? null;
    if (n === null) return;
    if (shownPage !== null && n !== shownPage) {
      // Dark drops the page-turn metaphor: pages cut instead of flip.
      turn = theme === 'dark' ? null : (n > shownPage ? 'forward' : 'back');
      clearTimeout(turnTimer);
      // The noteRelayout window: the class must leave again, or the next
      // turn could not re-trigger the animation.
      turnTimer = setTimeout(() => (turn = null), 400);
    }
    shownPage = n;
  });

  const previousPage = $derived(
    pages.find((p) => p.page_number === (page?.page_number ?? 1) - 1),
  );
  const nextPage = $derived(
    pages.find((p) => p.page_number === (page?.page_number ?? 1) + 1),
  );
  const laterActivePage = $derived(
    pages.find(
      (candidate) =>
        candidate.page_number > (page?.page_number ?? 0) &&
        Object.values(projection.tasks).some(
          (task) => task.page_id === candidate.id && task.state === 'Active',
        ),
    ),
  );
  const skippedPages = $derived(
    page?.status === 'Writing'
      ? []
      : pages.filter(
          (candidate) =>
            candidate.page_number > (page?.page_number ?? 0) &&
            candidate.status !== 'Writing' &&
            candidate.page_number < (laterActivePage?.page_number ?? Infinity) &&
            !Object.values(projection.tasks).some(
              (task) => task.page_id === candidate.id && task.state === 'Active',
            ),
        ),
  );
  const previousPageWasSkipped = $derived(
    previousPage !== undefined &&
      lastFooterSkippedPages.includes(previousPage.page_number),
  );

  const tasks = $derived(page ? tasksOnPage(projection, page.id) : []);
  const activeTasks = $derived(tasks.filter((t) => t.state === 'Active'));
  const activeCount = $derived(activeTasks.length);
  const isFinalPage = $derived(page?.status === 'Writing');

  /**
   * The task in flight, read off the PREDICTED projection.
   *
   * Predicted rather than committed because the takeover closes on *author*:
   * the row it is showing must already read as settled, with no round trip.
   */
  const takeoverTask = $derived(
    takeoverTaskId ? projection.tasks[takeoverTaskId] ?? null : null,
  );

  // ── the timeout checkpoint (STEP_6_SPEC.md D1, D5, D7) ──────────────────────

  /**
   * The view state (D1). The callbacks write it (open/close/resume/restart are
   * callback writes, which update the template reliably); the one effect write
   * is the tracker's mark: when the observed session TRANSITIONS into
   * TimedOut while the notebook is up, the hub takes over.
   *
   * The transition guard is load-bearing, not style: a hub exit publishes its
   * resolution (Resume/Restart/Leave-Active) ASYNCHRONOUSLY, so for one tick
   * the local cell still reads TimedOut while the view is already 'notebook'.
   * Re-firing on every read would flip the view straight back to the hub and
   * trap the user there — the no-checkpoint path (nothing resolved the mark
   * beforehand) hit exactly that. A transition fires once; the publish then
   * lands and the status moves off TimedOut.
   */
  let lastSessionStatus: AutofocusSession['status'] | null = null;
  $effect(() => {
    const status = session?.status ?? null;
    const previous = lastSessionStatus;
    lastSessionStatus = status;
    if (
      view === 'notebook' &&
      status === 'TimedOut' &&
      previous !== 'TimedOut'
    ) {
      view = 'hub';
    }
  });

  /**
   * Idea 14: the stage stays mounted under the hub — blurred, `inert` — so
   * the notebook branch's condition is no longer the hub branch's negation.
   * A named derived keeps the block's read explicit (the branch-effect quirk
   * the echo spans work around).
   */
  const stageMounted = $derived(view === 'notebook' || view === 'hub');

  /**
   * Idea 14: on a hub exit, focus returns to the page — the stage root —
   * instead of falling to `<body>`. The cleanup runs on the `view` flip,
   * after the render, so the stage's `inert` is already lifted by then and
   * the element is focusable again.
   */
  $effect(() => {
    if (view !== 'hub') return;
    return () => stageEl?.focus();
  });

  /** The store of the OPEN notebook — non-null whenever the notebook view is. */
  const store = $derived(activeStore!);

  /**
   * The checkpoint task, read off the PREDICTED projection: the session cell's
   * `current_task_id`, resolved only when the task is still ACTIVE.
   */
  const checkpointTaskId = $derived(session?.current_task_id ?? null);
  const checkpointTask = $derived(
    checkpointTaskId ? projection.tasks[checkpointTaskId] ?? null : null,
  );
  /**
   * D7: resolvable only while ACTIVE. A task another device settled during the
   * gap is already the answer — offering `[Finished]` for it would be a dead
   * button at best and a `TASK_ALREADY_SETTLED` notice at worst (Review
   * Focus #4).
   */
  const checkpointLive = $derived(checkpointTask?.state === 'Active');
  /**
   * The exit gate (D5). The degraded cases have nothing to resolve, so the
   * exits are live immediately; a live task resolves the moment a resolution's
   * publish lands, because the publish nulls `current_task_id`.
   */
  const checkpointResolved = $derived(!checkpointLive);

  /**
   * The banner copy names the task's OWN page (D7 case 1) — self-consistent
   * even when the session's page has drifted.
   */
  const checkpointPageNumber = $derived(
    checkpointTask ? projection.pages[checkpointTask.page_id]?.page_number ?? 1 : 1,
  );

  const SETTLED_WORDING: Record<'Done' | 'Reentered' | 'Dismissed', string> = {
    Done: 'finished',
    Reentered: 're-entered',
    Dismissed: 'dismissed',
  };

  /**
   * D7 cases 2–3, the hub renders a note instead of the banner: the task was
   * settled during the absence, or there was never a task to resolve. Null
   * while live, so the banner takes over.
   */
  const checkpointNote = $derived.by(() => {
    if (checkpointLive) return null;
    const task = checkpointTask;
    if (task && task.state !== 'Active') {
      return `You left off working on: ${task.title} — it has since been ${SETTLED_WORDING[task.state]}.`;
    }
    return 'Your session timed out.';
  });

  /** A18: the Guillotine's disarm trigger. */
  const itemsWorkedOnPage = $derived(session?.items_worked_on_page ?? 0);

  /**
   * Move focus to the next active task, wrapping at the ends.
   *
   * J off the bottom wraps to the first active task and K off the top wraps to
   * the last (idea R4.1, 2026-10-13 — reversing the earlier clamp-only rule).
   * The one exception is moving UP with nothing focused: there is no cursor to
   * wrap FROM, so it stays released rather than grabbing the last task.
   */
  function moveFocus(delta: number) {
    if (activeTasks.length === 0) {
      focusedTaskId = null;
      return;
    }
    const current = activeTasks.findIndex((t) => t.task_id === focusedTaskId);

    // Nothing focused and moving up: there is nothing above to wrap from, so
    // stay put. Wrapping to the last task would grab a cursor the user never
    // placed.
    if (current === -1 && delta < 0) {
      focusedTaskId = null;
      return;
    }

    const count = activeTasks.length;
    const next = (current + delta + count) % count;
    focusedTaskId = activeTasks[next]?.task_id ?? null;
  }

  function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
  }

  // ── the takeover (STEP_5_SPEC.md §3) ───────────────────────────────────────

  /**
   * D3: focus moves to the first ACTIVE task below the one just settled, off the
   * PREDICTED projection so the move is immediate.
   *
   * This still clamps rather than wraps — unlike J/K, which wrap (idea R4.1) —
   * because after a commit, jumping back to the first task would send the user
   * backwards through the page while they are sweeping forward. With no ACTIVE
   * task below, focus is released.
   */
  function advanceFocusFrom(taskId: string) {
    const settled = projection.tasks[taskId];
    if (!settled) {
      focusedTaskId = null;
      return;
    }
    const next = tasks.find((t) => t.state === 'Active' && t.line_index > settled.line_index);
    focusedTaskId = next?.task_id ?? null;
  }

  function openTakeover() {
    if (!focusedTaskId || !page) return;
    takeoverTaskId = focusedTaskId;
    // The page drawer closes: the takeover docks its own capture field, and two
    // open fields would both be stealing the caret.
    captureOpen = false;
    // ARCHITECTURE.md §3 specifies this write verbatim. `void` because focus
    // entry must never block on the network — `publishSession` writes the local
    // cell first and swallows a failed publish.
    void store.publishSession({
      current_page_number: page.page_number,
      current_task_id: focusedTaskId,
      status: 'Active',
    });
  }

  /**
   * R2.4: the mouse path to the takeover — the exact write the keyboard's
   * Space/Enter makes. Only a live, ASSIGNED task may take focus: a pending
   * prediction has no slot to work and a settled one nothing to do, so both do
   * nothing (TaskLine routes them out; this guard is the second gate).
   */
  function activateFromPage(taskId: string): void {
    if (pendingIds.has(taskId)) return;
    if (!activeTasks.some((t) => t.task_id === taskId)) return;
    focusedTaskId = taskId;
    openTakeover();
  }

  async function finishInFlight() {
    const taskId = takeoverTaskId;
    if (!taskId) return;
    // Author → close → advance, and only THEN publish.
    //
    // The order matters. Review Focus #1: the takeover must close on *author*,
    // not on acknowledgement — so nothing here may await the network, or an
    // unreachable relay would leave the user staring at a modal whose task they
    // already finished. The session write is the last step and is not awaited.
    await store.finishTask(taskId);
    takeoverTaskId = null;
    advanceFocusFrom(taskId);
    // `status` stays 'Active': AF1 continues the same page sweep.
    void store.publishSession({ current_task_id: null });
  }

  async function commitReentry(titles: string[]) {
    const taskId = takeoverTaskId;
    if (!taskId) return;
    await store.reenterTask(taskId, titles);
    noteReentryLanding(titles);
    reentryOpen = false;
    takeoverTaskId = null;
    advanceFocusFrom(taskId);
    void store.publishSession({ current_task_id: null });
  }

  /**
   * `[Re-enter]` always opens the sheet (user ruling 2026-10-05, amending idea
   * 12): the verbatim tap never bypassed the sheet — idea 12 was about the
   * sheet's OWN mode buttons, which are now gone, the wording starting exact.
   * The takeover keeps its three actions, and the sheet's commit closes it (D3).
   */
  function reenterInFlight() {
    reentryOpen = true;
  }

  /** Authors nothing at all — which is what makes it the escape from the takeover. */
  function stepBack() {
    takeoverTaskId = null;
    reentryOpen = false;
    void store.publishSession({ current_task_id: null, status: 'Paused' });
  }

  // ── the checkpoint resolutions (STEP_6_SPEC.md §4) ──────────────────────────

  /** Banner `[Finished]`: FinishTask, then the Paused patch. */
  async function resolveFinished() {
    const taskId = checkpointTaskId;
    if (!taskId) return;
    // Author → resolve → publish LAST and unawaited — the takeover's order.
    await store.finishTask(taskId);
    void store.publishSession({ current_task_id: null, status: 'Paused' });
  }

  /** Banner re-entry committed through the reused sheet (D6): one ReenterTask. */
  async function resolveReentry(titles: string[]) {
    const taskId = checkpointTaskId;
    if (!taskId) return;
    await store.reenterTask(taskId, titles);
    noteReentryLanding(titles);
    bannerReentryOpen = false;
    void store.publishSession({ current_task_id: null, status: 'Paused' });
  }

  /** Banner `[Leave Active on Page N]`: authors nothing, publishes the patch. */
  function resolveLeaveActive() {
    void store.publishSession({ current_task_id: null, status: 'Paused' });
  }

  /** Hub `[Resume Session at Page N]` (D5). */
  function resumeSession() {
    // The gate is the hub's aria-disabled; this is the routing. Resuming with
    // the task still set would publish a cell that says Active with a task in
    // flight, which no UI honours (Review Focus #5).
    if (!checkpointResolved) return;
    const page = session?.current_page_number;
    if (page == null) return;
    // The whole focus reset: the cell must not claim Active focus on whatever
    // current_task_id still names — in the degraded path (another device
    // settled the task during the gap) the exits go live without anything
    // having nulled it, and a cell that lies about focus is the one thing the
    // gate exists to stop (Review Focus #5).
    takeoverTaskId = null;
    reentryOpen = false;
    currentPageNumber = page;
    focusedTaskId = null;
    // Leave the hub: the exits are the way out (D1).
    view = 'notebook';
      // Deliberately NOT goToPage(N) — that helper now publishes the visit
      // itself (its own single-write comment); calling it here would
      // double-write the cell (two lamport bumps, two POSTs) for the same
      // state. The publish below IS the D5 patch.
    void store.publishSession({
      current_page_number: page,
      current_task_id: null,
      items_worked_on_page: 0,
      status: 'Active',
    });
  }

  /** Hub `[Restart Cycle at Page 1]` (D4): a session write, never a log op. */
  function restartCycle() {
    if (!checkpointResolved) return;
    const writingPage = pages.find((p) => p.status === 'Writing');
    const first = firstActivePage();
    // Same whole focus reset as Resume (Review Focus #5): the D4 patch is a
    // session write that must not carry a stale reference into Active.
    takeoverTaskId = null;
    reentryOpen = false;
    currentPageNumber = first;
    focusedTaskId = null;
    // Leave the hub: the exits are the way out (D1).
    view = 'notebook';
    void store.publishSession({
      current_page_number: first,
      current_task_id: null,
      // The boundary is re-asserted as the current writing page — the sweep's
      // §5.4 snapshot. The field's first writer.
      cycle_boundary_page_number: writingPage?.page_number ?? null,
      items_worked_on_page: 0,
      status: 'Active',
    });
  }

  function onKeydown(event: KeyboardEvent) {
    // The banner's sheet is the more specific surface, hub view or not: its
    // own Escape handler already ran and stopped propagation, and this covers
    // focus sitting on one of the hub's controls. Escape cancels the sheet.
    if (bannerReentryOpen) {
      if (event.key === 'Escape') {
        event.preventDefault();
        bannerReentryOpen = false;
      }
      return;
    }

    // Step 8 (D8): the read-only views. `Esc` on a typing target blurs it
    // first (the capture field's two-stage rule), `Esc` elsewhere returns to
    // the notebook, and EVERY other key is inert — the stage the notebook
    // bindings act on is not rendered here. No `preventDefault` on the inert
    // keys, so a focused button (e.g. `[ Back ]`) still activates on
    // Space/Enter.
    if (view === 'browse' || view === 'graveyard' || view === 'debuglog') {
      if (event.key === 'Escape') {
        if (isTypingTarget(event.target)) {
          (event.target as HTMLElement).blur();
          return;
        }
        event.preventDefault();
        backToNotebook();
      }
      return;
    }

    // D1 + Step 7: while the LIST is up the keyboard layer is equally DEAD —
    // the stage it navigates is not rendered. Returning before any
    // preventDefault keeps the hub's own buttons operable by keyboard.
    if (view !== 'notebook') return;

    if (isTypingTarget(event.target)) {
      // Only Escape escapes a field.
      if (event.key === 'Escape') {
        captureOpen = false;
        (event.target as HTMLElement).blur();
      }
      return;
    }

    // The sheet is the more specific surface: its own Escape handler already ran
    // and stopped propagation, and this covers the case where focus sat on one of
    // its non-input controls. Escape cancels the sheet first; a second one steps
    // back out of the takeover.
    if (reentryOpen) {
      if (event.key === 'Escape') {
        event.preventDefault();
        reentryOpen = false;
      }
      return;
    }

    // The takeover owns the keyboard while it is open. `inert` takes the page out
    // of reach for the pointer, but this listener is on `window`, so it has to
    // decline the page's keys itself. Escape is NOT prevented otherwise, so a
    // focused button inside the modal still activates on Space/Enter.
    if (takeoverTaskId !== null) {
      if (event.key === 'Escape') {
        event.preventDefault();
        stepBack();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
      case 'j':
      case 'J':
        event.preventDefault();
        moveFocus(1);
        break;
      case 'ArrowUp':
      case 'k':
      case 'K':
        event.preventDefault();
        moveFocus(-1);
        break;
      case ' ':
      case 'Enter': {
        if (!focusedTaskId) return;
        event.preventDefault();
        // Opens the takeover rather than finishing the task outright (D2). The
        // op is authored from inside it, so there is still a chance to re-enter
        // an unfinished task BEFORE crossing it off.
        openTakeover();
        break;
      }
      case 'c':
      case 'C':
        event.preventDefault();
        captureOpen = !captureOpen;
        break;
      case 'e':
      case 'E': {
        // R4.6: edit the last item you typed in — the drawer re-opens prefilled
        // and Enter rewords instead of appending. Only within reach: the reword
        // goes through this notebook's store.
        if (!lastCaptured || lastCaptured.notebookId !== store.identity.notebook_id) return;
        event.preventDefault();
        editTarget = lastCaptured;
        captureOpen = true;
        break;
      }
      case 'Escape':
        event.preventDefault();
        captureOpen = false;
        focusedTaskId = null;
        break;
      default:
        break;
    }
  }

  // ── the undo snackbar (STEP_8_SPEC.md D1, D10) ──────────────────────────────

  /** How long the dismissal stays undoable, in milliseconds. */
  const UNDO_WINDOW_MS = 8000;

  /** How often the VISIBLE countdown is refreshed. The clock is not read here. */
  const UNDO_TICK_MS = 250;

  interface ArmedDismiss {
    pageId: string;
    pageNumber: number;
    count: number;
    /**
     * Absolute wall-clock deadline. Since R4.7 this drives BOTH the expiry
     * `setTimeout` and the display ticker below — one clock, read two ways.
     */
    expiresAt: number;
  }

  /**
   * The armed dismissal, or null. Pure component state (D1): nothing is in
   * the outbox while it is armed, so a reload loses nothing durable — the
   * safe failure direction.
   */
  let armedDismiss = $state<ArmedDismiss | null>(null);
  /** The one countdown timer (D1). Cleared on undo, re-arm, cancel, unmount. */
  let armTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * R4.7: the display ticker. It only re-reads `expiresAt` to refresh the
   * snackbar's number (the TomatoTimer deadline pattern) — it authors nothing,
   * and the `setTimeout` above remains the one thing that expires the arm.
   */
  let armTicker: ReturnType<typeof setInterval> | null = null;
  /** Whole seconds left in the armed window, for the snackbar's label. */
  let secondsLeft = $state(0);

  function clearArm(): void {
    if (armTimer) {
      clearTimeout(armTimer);
      armTimer = null;
    }
    if (armTicker) {
      clearInterval(armTicker);
      armTicker = null;
    }
    armedDismiss = null;
  }

  /**
   * The footer's dismissal request now ARMS rather than authors (D1). At
   * expiry the real op — the one the user committed to at tap time — enters
   * the outbox. A second tap re-arms with a fresh window (D10).
   *
   * Idea 16 (user ruling 2026-10-05): arming also moves the user to the next
   * page, so they are LOOKING AT it while the countdown runs. The arm captured
   * the page at tap time, so the navigation cannot retarget the dismissal —
   * and navigating no longer cancels the arm (amended D10): only leaving the
   * view or working an item does.
   */
  function armDismiss(): void {
    if (!page) return;
    clearArm();
    const expiresAt = Date.now() + UNDO_WINDOW_MS;
    armedDismiss = {
      pageId: page.id,
      pageNumber: page.page_number,
      count: activeCount,
      expiresAt,
    };
    secondsLeft = Math.ceil(UNDO_WINDOW_MS / 1000);
    armTicker = setInterval(() => {
      const arm = armedDismiss;
      if (!arm) return;
      secondsLeft = Math.max(0, Math.ceil((arm.expiresAt - Date.now()) / 1000));
    }, UNDO_TICK_MS);
    armTimer = setTimeout(() => {
      const arm = armedDismiss;
      if (!arm) return;
      // clearArm stops the display ticker too; the op below is the only thing
      // that actually dismisses the page.
      clearArm();
      void store.dismissPage(arm.pageId);
    }, UNDO_WINDOW_MS);
    // R5.10 (user ruling 2026-10-16): the arm advances like the footer does —
    // past any page with nothing to action (already dismissed, or nothing
    // left active). The old target was the literal adjacent page, which the
    // sweep could already have passed; the user landed on a dead page.
    void goToPage(footerNextPage());
  }

  /** `[ Undo ]`: clears the arm and authors nothing (D1). */
  function undoDismiss(): void {
    clearArm();
  }

  // D10, as amended 2026-10-05 (user ruling): the arm SURVIVES page
  // navigation — arming navigates to the next page and the countdown keeps
  // running there, because the arm captured the page at tap time and expiry
  // authors against that. The effect's reads ARE the watch: leaving the view
  // (any non-notebook view) or working an item (A18) still clears the arm
  // before the expiry can fire. Reading the page here would cancel the arm on
  // its own arming navigation — that read is deliberately gone.
  $effect(() => {
    const arm = armedDismiss;
    if (!arm) return;
    if (view !== 'notebook' || itemsWorkedOnPage > 0) {
      clearArm();
    }
  });

  // The timer must not outlive the component.
  $effect(() => {
    return () => clearArm();
  });

  // ── browse / graveyard (STEP_8_SPEC.md D5, D8) ──────────────────────────────

  /**
   * Entering a read-only view writes no session field and authors no op —
   * unlike `goToPage`, which publishes the visit (`publishSession`) and stays
   * notebook-only. The drawer is closed because its state is visit-scoped:
   * returning from browse should not resurrect a capture the user left behind.
   */
  function openBrowse(): void {
    view = 'browse';
    focusedTaskId = null;
    captureOpen = false;
  }

  /** `Esc` and the `[ Back ]` buttons both land here; authors nothing. */
  function backToNotebook(): void {
    view = 'notebook';
  }

  /** Same visit-scoped reset as `openBrowse` (D8). */
  function openGraveyard(): void {
    view = 'graveyard';
    focusedTaskId = null;
    captureOpen = false;
  }

  /**
   * The debug log (IDEAS.md #8): same read-only pattern as browse/graveyard —
   * button-reached, keyboard dead, authors nothing. It reads the RELAY's log
   * (not the local projection), so it needs the transport, not the store.
   */
  function openDebugLog(): void {
    view = 'debuglog';
    focusedTaskId = null;
    captureOpen = false;
  }

  /**
   * R3.3: the developer seeder the debug log hands its count to. The pool is
   * plausible FAKE TASKS — errands, work items and home chores at varied
   * lengths, so the J/K focus layer, line wrapping and relayout all meet
   * realistic content. Each item is a REAL AppendTask through the same path
   * capture uses (`appendTask` mints a UUIDv7 and queues the outbox
   * envelope) — local row injection would defeat the point, which is
   * testing sync.
   */
  const SEED_TASKS = [
    'Water the ferns',
    'Book the dentist appointment',
    'Draft the sync agenda',
    'Renew the car insurance',
    'Fix the wobbly chair',
    'Reply to Alice about the venue for the Saturday birthday dinner',
    'Sort the garage shelves',
    'Cancel the unused subscription',
    'Order toner for the printer',
    'Plan the team offsite',
    'Return the library books',
    'Update the on-call rota',
    'Back up the laptop',
    'Choose a birthday card for Sam',
    'File the expense report',
    'Test the new keyboard shortcuts',
    'Sketch the landing page layout',
    'Refill the bird feeder',
    'Compare broadband deals',
    'Write the sprint retro notes',
  ];

  async function seedTasks(count: number): Promise<void> {
    for (let i = 0; i < count; i += 1) {
      await store.appendTask(SEED_TASKS[i % SEED_TASKS.length]!);
    }
    // The view's reload (which follows onSeed) reads the relay's log — so the
    // drain must have happened by then, not 50ms later on the debounce timer.
    await store.flushNow(store.transport);
  }

  async function goToPage(pageNumber: number, skippedPageNumbers: number[] = []) {
    currentPageNumber = pageNumber;
    lastFooterSkippedPages = skippedPageNumbers;
    focusedTaskId = null;
    // A fresh look at a page re-arms the guillotine for that visit (A18: the
    // reset is what entering a page means) — and the visit TRAVELS: this is a
    // session-cell change, so it publishes. ONE write, ONE POST via
    // `publishSession` — the same single-write rule the hub's Resume follows.
    void store.publishSession({
      current_page_number: pageNumber,
      items_worked_on_page: 0,
    });
  }

  /**
   * R2.1 fix-round: leave the not-found view cleanly. The visual re-render is
   * already safe — every `applyPath` entry rewrites both fields before the
   * branch reads them — but Task 3's popstate path can land at `notfound`
   * differently (e.g. on top of a stale re-entry), and a stale pair there
   * would render last-known copy under a fresh URL. Clear on exit so the
   * invariant "every `notfound` render was written by `applyPath`" holds.
   */
  function backFromNotFound(): void {
    notFoundPath = '';
    notFoundTag = null;
    view = 'list';
  }

  /**
   * R3.2 §3: the settings page is a button-reached view state — no hotkey,
   * and the keyboard layer is dead there (the `view !== 'notebook'` early
   * return in `onKeydown`). Its exit returns to the list.
   */
  function openSettings(): void {
    view = 'settings';
  }

  function backToList(): void {
    view = 'list';
  }

  /**
   * R4.9 §7 (Task 11): the admin page is a sub-view of settings — reached
   * from its `[ Admin ]` entry, and its `[ Back ]` returns there. A button-reached
   * view state, like settings: no hotkey, and the keyboard layer is dead on it.
   */
  function openAdmin(): void {
    view = 'admin';
  }

  function backFromAdmin(): void {
    view = 'settings';
  }

  /**
   * A local half of sign-out that refused, or null.
   *
   * `signOut` cannot fail at the RELAY — that call is best-effort inside it — so
   * whatever escapes it is this browser's own half: the owner switch, the cache
   * clear, or the sweep of the other owners' leftover stores. The transition
   * completes either way (see `signOutNow`), and this is where the failure is
   * said out loud instead of vanishing into an unhandled rejection.
   */
  let signOutError = $state<string | null>(null);

  /**
   * `[ Sign out ]` (spec §5): revoke the login, forget who this browser was, and
   * put up the sign-in view the relay's own `401` would have produced.
   *
   * `signOut()` owns the durable half — the logout POST, clearing the cached
   * account, and collecting the other owners' leftover stores so the next person
   * on this browser cannot inherit them. What is left for the container is the
   * live page: the stores it holds belong to the account that just left, and the
   * owner switch staled them at the database level (db.ts), so they are dropped
   * rather than left to render. The app is nobody's again, and the gate keeps it
   * that way.
   *
   * The transition is therefore unconditional, and a rejection from the local
   * half does not stop it. That half moves storage towards the anonymous owner
   * before it can fail, so abandoning the sequence would leave the panel showing
   * an account the storage has already stopped acting as — an app that renders
   * somebody it is no longer addressing. Nothing here can repair the storage; the
   * honest failure direction is to finish becoming nobody and say what failed.
   */
  async function signOutNow(): Promise<void> {
    try {
      await signOut();
    } catch (e) {
      signOutError = e instanceof Error ? e.message : String(e);
    }
    signedOut = true;
    // Both writes land: `view` puts the sign-in view up NOW, and the gate the
    // boot effect reads (below) is what keeps every other path out of the app
    // from here on.
    activeStore = null;
    stores.clear();
    view = 'signin';
  }

  /**
   * R5.5: the account-deletion confirm modal, opened from the account page
   * (admins) or the settings sign-out footer (members) — the two surfaces
   * where sign-out lives, because a deletion IS the sign-out that follows.
   * The modal mounts as a sibling with both hosts `inert` behind it.
   */
  let deleteAccountOpen = $state(false);

  function requestDeleteAccount(): void {
    deleteAccountOpen = true;
  }

  function cancelDeleteAccount(): void {
    deleteAccountOpen = false;
  }

  /**
   * The deletion itself: the relay purges the account and everything attached
   * to it in one transaction (R5.5's server ruling), then this browser signs
   * out — the same sequence `signOutNow` runs, since the account it acted as
   * no longer exists. A relay failure surfaces on the rejection-toast path
   * with the modal closed; the account survives, and nothing local is lost.
   */
  let deleteAccountInFlight = $state(false);
  async function commitDeleteAccount(): Promise<void> {
    if (deleteAccountInFlight || !signedIn) return;
    deleteAccountInFlight = true;
    deleteAccountOpen = false;
    try {
      await deleteAccount();
      await signOut();
    } catch (e) {
      rejections = [
        ...rejections,
        {
          op_id: 'account',
          reason: e instanceof Error ? e.message : String(e),
        },
      ];
      deleteAccountInFlight = false;
      return;
    }
    deleteAccountInFlight = false;
    signedOut = true;
    activeStore = null;
    stores.clear();
    view = 'signin';
  }

  /**
   * R4.9 §7 (Task 8): the sign-in view is a button-reached view state like
   * settings — no hotkey, and the keyboard layer is dead there. Reached from the
   * list's `[ Sign in ]` entry on an unclaimed relay with Google configured, or
   * from `/signin`; the gate puts it up directly, with no way back, when the
   * relay has accounts and nobody is signed in.
   */
  function openSignIn(): void {
    view = 'signin';
  }

  /**
   * Leaves the sign-in view the way the user came in: back to the notebook that
   * was open behind it, or to the list. Never reached on the gate's own sign-in
   * view — there is nothing behind that one.
   */
  function backFromSignIn(): void {
    view = activeStore ? 'notebook' : 'list';
  }

  function requestDelete(id: string): void {
    deleteTarget = notebooks.find((n) => n.notebook_id === id) ?? null;
  }

  /** Closes the modal, authoring nothing — Esc and Cancel both land here. */
  function cancelDelete(): void {
    deleteTarget = null;
  }

  /**
   * R3.2 §2, evict-then-drop: close the takeover/sheet, then the relay purge,
   * the local drop, the list refresh, and back to the list. The snackbar arm
   * dies with the view exit (the existing D10 rule — no new code). The store
   * leaves the container's map so no stale RenderModel renders. A relay
   * failure surfaces on the rejection-toast path; success carries no toast —
   * the list without the notebook IS the confirmation.
   */
  async function commitDelete(): Promise<void> {
    const target = deleteTarget;
    if (!target) return;
    deleteTarget = null;
    takeoverTaskId = null;
    reentryOpen = false;
    bannerReentryOpen = false;
    try {
      await deleteNotebook(target.notebook_id);
    } catch (e) {
      rejections = [
        ...rejections,
        { op_id: target.notebook_id, reason: e instanceof Error ? e.message : String(e) },
      ];
      return;
    }
    stores.delete(target.notebook_id);
    if (activeStore?.identity.notebook_id === target.notebook_id) activeStore = null;
    await dropLocalNotebook(target.notebook_id);
    await refreshList();
    view = 'list';
  }

  /** Applies a relayout diff to the pending rows that moved (§6.3). */
  export function noteRelayout(ids: string[]) {
    // Silence in the common case is the goal: a toast only when something moved.
    if (ids.length === 0) return;
    relayoutIds = new Set(ids);
    setTimeout(() => (relayoutIds = new Set()), 400);
  }


</script>

<svelte:window onkeydown={onKeydown} />

<!--
  D1: the LIST is the front door — the container mounts at it when no notebook
  was provided. The echo span gives the branch a reactive reader of its own
  condition (the same block-effect quirk the hub branch works around below).
-->
{#if view === 'list'}
  <span data-testid="list-view-echo" hidden>{view}</span>
  <NotebookListView
    {notebooks}
    onOpen={(id) => void openNotebook(id)}
    onCreate={openCreate}
    onEdit={openEdit}
    onSettings={openSettings}
    onSignIn={signinOffered ? openSignIn : undefined}
  />
  <!--
    R3.2 §2: the one-line notice after evicting a notebook deleted elsewhere.
    One state, one site; Dismiss clears it in place.
  -->
  {#if deletedNotice !== null}
    <div class="toast rejection-notice" data-testid="deleted-notice" aria-live="polite">
      <span>{deletedNotice}</span>
      <button class="action" type="button" onclick={() => (deletedNotice = null)}>
        <Icon node={X} />Dismiss
      </button>
    </div>
  {/if}
{/if}

<!--
  R4.9 §7 (Task 8): the sign-in view. Two ways in — the gate (the relay has
  accounts and this browser has no live login: nothing else is reachable, and the
  Back exit is withheld because there is nowhere to go), and the list's
  `[ Sign in ]` entry on an unclaimed relay. Never rendered when sign-in is
  unavailable (D2): both branches require `signin_available`, and the entry is
  hidden without it.
-->
{#if view === 'signin' && gate}
  <span data-testid="signin-view-echo" hidden>{view}</span>
  <SignInView auth={gate} onBack={signinRequired ? undefined : backFromSignIn} />
{/if}

<!--
  R4.9 §7 (Task 11): the admin page — the signed-in email, `[ Sign out ]`, and
  the invite/user-list half. Admin-only: reached from settings' `[ Admin ]` entry
  and addressable at `/settings/admin` (`applyPath` corrects any other path —
  nobody signed in, or a member, who has sign-out on settings instead — to a page
  they do have); the keyboard layer is dead here, like every other
  button-reached view. The account riding in is the one the gate RESOLVED
  (`signedIn`), never a placeholder.
-->
{#if view === 'admin' && signedIn}
  <span data-testid="admin-view-echo" hidden>{view}</span>
  <div inert={deleteAccountOpen}>
    <AdminPanel
      account={signedIn}
      onBack={backFromAdmin}
      onSignOut={() => void signOutNow()}
      onDeleteAccount={requestDeleteAccount}
    />
  </div>

  {#if rejections.length > 0}
    <div class="toast rejection-notice" data-testid="rejection-notice" aria-live="polite">
      <span>{rejections[rejections.length - 1]!.reason}</span>
      <button class="action" type="button" onclick={() => (rejections = [])}><Icon node={X} />Dismiss</button>
    </div>
  {/if}
{/if}

<!--
  R3.2 §3: the settings page — a button-reached view state like
  browse/graveyard (no hotkeys, keyboard layer dead, authors nothing but the
  delete). Echo span as in the hub branch: the branch needs a reactive reader
  of its own condition across callback writes. The delete modal mounts as a
  sibling with the page `inert` behind it (the creation modal's contract).
-->
{#if view === 'settings'}
  <span data-testid="settings-view-echo" hidden>{view}</span>
  <div class="settings-host" inert={deleteTarget !== null || createModalOpen || deleteAccountOpen}>
    <SettingsView
      {notebooks}
      onBack={backToList}
      onDeleteRequest={requestDelete}
      onEditRequest={openEdit}
      onImport={(file) => void importNotebookFile(file)}
      account={signedIn}
      onAdmin={openAdmin}
      onSignOut={() => void signOutNow()}
      onDeleteAccount={requestDeleteAccount}
      ownerId={owner_id}
      {theme}
      onThemeChange={setTheme}
    />
  </div>

  <!-- IDEAS 10: the import surface's own notice — a transfer failure, not an
       op rejection (the outbox's notices stay separate). One state, one site;
       the Dismiss clears it in place. -->
  {#if importError !== null}
    <div class="toast rejection-notice" data-testid="import-notice" aria-live="polite">
      <span>{importError}</span>
      <button class="action" type="button" onclick={() => (importError = null)}>
        <Icon node={X} />Dismiss
      </button>
    </div>
  {/if}

  {#if rejections.length > 0}
    <div class="toast rejection-notice" data-testid="rejection-notice" aria-live="polite">
      <span>{rejections[rejections.length - 1]!.reason}</span>
      <button class="action" type="button" onclick={() => (rejections = [])}><Icon node={X} />Dismiss</button>
    </div>
  {/if}

  {#if deleteTarget}
    <DeleteNotebookModal
      notebook={deleteTarget}
      onConfirm={() => void commitDelete()}
      onCancel={cancelDelete}
      ownerId={owner_id}
    />
  {/if}
{/if}

<!--
  R2.1, Task 2: the not-found view (spec §4.3). An in-stage panel with the
  same list-view chrome — it sits where the list sat, no router involved.
  The tag copy and the generic copy are disjoint by `notFoundTag`, so the
  branch renders one line or the other, never both.
-->
{#if view === 'notfound'}
  <span data-testid="notfound-view-echo" hidden>{view}</span>
  <div class="notebook-list" data-testid="notfound-view">
    <p data-testid="notfound-copy">
      {#if notFoundTag !== null}
        There's no notebook with the tag {notFoundTag}.
      {:else}
        We can't find that page.
      {/if}
    </p>
    <p><code>{notFoundPath}</code></p>
    <button
      class="action"
      type="button"
      data-testid="notfound-back"
      onclick={backFromNotFound}
    >
      [ Back to list ]
    </button>
  </div>
{/if}

<!--
  D1: the hub takes over from the notebook while a timed-out session is
  unresolved — `view` is written by the callbacks and the tracker's mark.
  Idea 14 (ARCHITECTURE.md §6: assume the user is not gaming the system): the
  hub renders ABOVE the last page, which stays mounted behind a blur, `inert`
  — the takeover's own contract (D4), not the next-page margin's.
-->
{#if view === 'hub'}
  <!--
    A tracked template read of the view signal inside this branch. Without it
    the branch's block effect never re-runs on a Resume/Restart flip (the hub
    stays mounted forever) under Svelte 5.57: the block condition alone does
    not keep the branch subscribed across the callback write. The echo is
    hidden and inert; it exists so the branch has a reactive reader of its own
    condition.
  -->
  <span data-testid="hub-view-echo" hidden>{view}</span>
  <!-- D8: the hub stays present but not interactive under the creation modal. -->
  <div class="hub-host" inert={createModalOpen}>
    <LandingHub
      notebookName={projection.notebook.name}
      cycleBoundary={session?.cycle_boundary_page_number ?? null}
      resumePageNumber={session && session.lamport > 1 ? session.current_page_number : null}
      restartPageNumber={firstActivePage()}
      checkpointTask={checkpointLive ? checkpointTask : null}
      {checkpointPageNumber}
      {checkpointNote}
      {checkpointResolved}
      onResume={resumeSession}
      onRestartCycle={restartCycle}
      onFinished={resolveFinished}
      onReenter={() => (bannerReentryOpen = true)}
      onLeaveActive={resolveLeaveActive}
    />
  </div>

  <!-- D6: the banner's re-entry reuses the sheet, unchanged, with the
       checkpoint task — the same one-ReenterTask-never-N-appends rule. -->
  {#if bannerReentryOpen && checkpointTask}
    <ReentryBottomSheet
      task={checkpointTask}
      onCommit={resolveReentry}
      onCancel={() => (bannerReentryOpen = false)}
    />
  {/if}
{/if}

<!-- The notebook branch: its own block effect. Branch managers keying two
     branches of one {#if}/{:else} left the hub mounted after an exit navigated
     (Svelte 5.57); separate blocks each reading `view` swap reliably. Since
     idea 14 the conditions are no longer complementary — this branch also
     stays mounted UNDER the hub, veiled — but each block still reads `view`
     itself. -->
{#if stageMounted}
<!--
  D4: while the takeover is open the stage is `inert` AND blurred. Idea 14
  extends the same contract to the hub: the page stays mounted under it,
  `inert` while the hub is up, the blur decorating.

  `inert` is what actually takes the page out of reach. The blur is decoration:
  unlike the next-page margin — where a blur would leave future task text
  readable in the DOM and the accessibility tree, which is why it was rejected
  there — the current page's content is already legitimately in the DOM and is
  not a leak. The rule that survives both cases is that blur decorates and
  `inert` controls. `tabindex="-1"` makes the stage the page's focus target:
  the hub-exit effect returns focus here when the hub goes away.
-->
<div
  class="notebook-stage"
  bind:this={stageEl}
  tabindex="-1"
  inert={takeoverTaskId !== null || createModalOpen || view === 'hub'}
  class:notebook-stage--blurred={takeoverTaskId !== null || createModalOpen || view === 'hub'}
>
  <div class="notebook-spread">
    <!-- Previous-page margin: page edge only. -->
    <NextPageMargin
      which="previous"
      slots={capacity}
      pageNumber={previousPage?.page_number}
      dogEared={previousPageWasSkipped}
    />

    {#if page}
      <PageCard
        {page}
        {projection}
        {pendingIds}
        {relayoutIds}
        {turn}
        focusedTaskId={focusedTaskId}
        {isFinalPage}
        notebookName={projection.notebook.name}
        notebookTag={projection.notebook.tag}
        onActivateTask={activateFromPage}
      />
    {/if}

    <!--
      Next-page margin: page geometry only. It receives no task content at all —
      see NextPageMargin for why that is a security boundary rather than taste.
    -->
    <!-- No fallback: no next page (the Final page) renders no label, so the
         margin never invents a "Page N" for a page that does not exist. -->
    <NextPageMargin
      which="next"
      slots={capacity}
      pageNumber={nextPage?.page_number}
      dogEared={skippedPages.some((skipped) => skipped.page_number === nextPage?.page_number)}
      lastSkippedPageNumber={skippedPages.at(-1)?.page_number}
    />
  </div>

  <PageFooter
    {itemsWorkedOnPage}
    {activeCount}
    {isFinalPage}
    onDismiss={armDismiss}
    onNext={advanceFromFooter}
    onClose={closeNotebook}
    onBrowse={openBrowse}
    onGraveyard={openGraveyard}
    onDebugLog={openDebugLog}
    onCapture={() => (captureOpen = true)}
    editArmed={lastCaptured !== null && lastCaptured.notebookId === store.identity.notebook_id}
  />

  {#if captureOpen}
    <InFlightQuickCapture
      bind:value={captureText}
      {store}
      editing={editTarget}
      onCommitted={(taskId, title) => {
        lastCaptured = { taskId, title, notebookId: store.identity.notebook_id };
        noteOffscreenLanding(title);
      }}
      onClose={() => {
        captureOpen = false;
        editTarget = null;
      }}
      onCaptureToNotebook={routeCapture}
      notebookTags={notebooks.map((n) => ({ tag: n.tag, name: n.name }))}
      onCreateNotebook={(tag: string, title: string) => {
        capturedTag = tag;
        capturedFirstTask = title;
        capturedModalOpen();
      }}
    />
  {/if}
</div>

<!--
  A SIBLING of `.notebook-stage`, never inside it (D4): inside, the `inert` above
  would disable the takeover itself along with the page. D8: when the creation
  modal is up, the takeover is inert too — keyboard focus cannot reach behind
  the modal into Finished/Re-enter/Step back. Idea 14: it renders only in the
  notebook view — a wake during an open takeover leaves nothing floating over
  the hub (the hub is the focused surface then).
-->
<div inert={createModalOpen}>
{#if view === 'notebook' && takeoverTask && takeoverTaskId}
  <FocusTakeoverModal
    {store}
    task={takeoverTask}
    pageNumber={page?.page_number ?? 1}
    {rejections}
    onFinished={finishInFlight}
    onReenter={reenterInFlight}
    onStepBack={stepBack}
    onDismissNotice={() => (rejections = [])}
    onCaptureToNotebook={routeCapture}
    lastCaptured={lastCaptured}
    onCommitted={(taskId, title) => {
      lastCaptured = { taskId, title, notebookId: store.identity.notebook_id };
      noteOffscreenLanding(title);
    }}
    notebookTags={notebooks.map((n) => ({ tag: n.tag, name: n.name }))}
    onCreateNotebook={(tag: string, title: string) => {
      capturedTag = tag;
      capturedFirstTask = title;
      capturedModalOpen();
    }}
  />
{/if}
</div>

{#if view === 'notebook' && reentryOpen && takeoverTask}
  <ReentryBottomSheet
    task={takeoverTask}
    onCommit={commitReentry}
    onCancel={() => (reentryOpen = false)}
  />
{/if}

<!--
  The same notice the takeover shows, for a verdict that lands after it closed.
  One toast in one of two places, never two toasts (§0.3).
-->
{#if view === 'notebook' && rejections.length > 0 && takeoverTaskId === null}
  <div class="toast rejection-notice" data-testid="rejection-notice" aria-live="polite">
    <span>{rejections[rejections.length - 1]!.reason}</span>
    <button class="action" type="button" onclick={() => (rejections = [])}><Icon node={X} />Dismiss</button>
  </div>
{/if}

<!--
  The capture-saved notice (IDEAS 11, amended 2026-10-16): ONE state, ONE site —
  a toast pinned to the bottom right, rendered whether or not the takeover is
  open. It used to render as a banner at the top of the takeover, which pushed
  the task title down — the notice must never move the task the user is working
  on. It now fires whenever the landed capture is not on the page on screen: a
  cross-notebook route names the notebook, a same-notebook landing on the Final
  page names the page.
-->
{#if view === 'notebook' && (captureToasts.length > 0 || armedDismiss)}
  <!-- The pile: bottom-right, newest last. Each toast expires on its own;
       the dismiss button is the quiet escape hatch, not a call to action.
       The undo snackbar piles here too (2026-10-16 user ruling, amending
       idea 16's left-of-footer placement): D1 — the page renders unchanged
       until expiry actually authors the op. -->
  <div class="toast-stack">
    {#each captureToasts as toast (toast.id)}
      {@const link = splitTitleLink(toast.title)}
      <div
        class="toast capture-saved-notice"
        class:toast--leaving={toast.leaving}
        data-testid="capture-saved-notice"
        aria-live="polite"
      >
        <!-- R5.6 amended: the toast shortens a URL exactly like the ruled
             line — the bracketed link, the full address on the tooltip. -->
        <span
          >Saved to {toast.label} — “{#if link}{link.before}<a
            class="title-link"
            href={link.url}
            title={link.url}
            rel="noopener noreferrer"
            target="_blank"
            tabindex="-1"
            >[link]</a
          >{link.after}{:else}{toast.title}{/if}”</span
        >
        <button
          class="action action--quiet"
          type="button"
          aria-label="Dismiss"
          title="Dismiss"
          onclick={() => dismissToast(toast.id)}
        >
          <Icon node={X} />
        </button>
      </div>
    {/each}
    {#if armedDismiss}
      <UndoSnackbar count={armedDismiss.count} {secondsLeft} onUndo={undoDismiss} />
    {/if}
  </div>
{/if}
{/if}

<!--
  Step 7: the notice also renders at the LIST — the §3 collision flow's flush
  can land while the user is there (the store stays mounted after [Close]), and
  the loser's TAG_TAKEN is exactly what they need to see.
-->
{#if view === 'list' && rejections.length > 0}
  <div class="toast rejection-notice" data-testid="rejection-notice" aria-live="polite">
    <span>{rejections[rejections.length - 1]!.reason}</span>
    <button class="action" type="button" onclick={() => (rejections = [])}><Icon node={X} />Dismiss</button>
  </div>
{/if}

<!--
  R19 / spec §5: "Session expired — sign in again". The login the app booted with
  can be refused mid-page, and the outbox then pauses with the user's work intact
  (`authRequired`, operations.ts) — silently, until this. It renders above ANY
  view, because the refusal can land from a background drain while the user is on
  the list or in another notebook, and its one action enters the EXISTING sign-in
  view rather than a second login implementation. Hidden while that view is up:
  it would be the same offer twice.
-->
{#if authExpired && view !== 'signin'}
  <div class="toast auth-expired-notice" data-testid="auth-expired-notice" aria-live="polite">
    <!-- The copy is spec §5's verbatim; it is a user-facing string, not this
         codebase's name for auth state (that is never "session" — the focus cell
         is). -->
    <span>Session expired — sign in again</span>
    <!-- The message stands on its own (it is the only explanation of a stalled
         drain), but the way in needs a verdict to sign IN to. A mount with no
         gate has none — the same D2 rule that keeps every other sign-in
         affordance off it — so it offers no dead-end button. -->
    {#if auth !== null}
      <button class="action" type="button" data-testid="auth-expired-signin" onclick={openSignIn}>
        [ Sign in ]
      </button>
    {/if}
  </div>
{/if}

<!--
  R5.15 (user ruling 2026-10-16): the offline notice — the relay unreachable or
  the browser offline. The store owns the flag; this is the one render site,
  shaped exactly like the auth-expired banner above: a message that stands on
  its own (the drain retries by itself), so it offers no button.
-->
{#if offline && view !== 'signin'}
  <div class="toast offline-notice" data-testid="offline-notice" aria-live="polite">
    <span>Offline — changes sync when the relay answers</span>
  </div>
{/if}

<!--
  R4.9 §7 (Task 11): signing out has two halves — the relay's `POST /api/auth/logout`,
  which is best-effort, and this browser's own (the owner switch, the cache clear,
  the leftover-store sweep). Only the second can fail, and the transition above
  goes ahead regardless, so this is the one place that failure is visible: the
  person lands on the sign-in view knowing their local data may not be fully
  cleared, rather than the app showing an account it no longer acts as.
-->
{#if signOutError !== null}
  <div class="toast rejection-notice" data-testid="signout-notice" aria-live="polite">
    <span>Signed out, but this browser could not finish clearing its local data: {signOutError}</span>
    <button class="action" type="button" onclick={() => (signOutError = null)}>
      <Icon node={X} />Dismiss
    </button>
  </div>
{/if}

<!--
  Step 8 (D8): browse and graveyard are view states parallel to the hub — no
  router. Reached only from inside a notebook (the footer buttons); the
  keyboard layer is dead in both. Echo spans as in the hub branch: the branch
  needs a reactive reader of its own condition across callback writes.
-->
{#if view === 'browse'}
  <span data-testid="browse-view-echo" hidden>{view}</span>
  <BrowseModeView
    {projection}
    {pendingIds}
    onBack={backToNotebook}
  />
{/if}

{#if view === 'graveyard'}
  <span data-testid="graveyard-view-echo" hidden>{view}</span>
  <!--
    The store rides in (D4): the commit authors through it, and the branch
    mounts the shared `rejection-notice` — one list, drained by the same
    `drainRejections()`, one notice never two (Review Focus 5).
  -->
  <GraveyardView {projection} {store} onBack={backToNotebook} />

  {#if rejections.length > 0}
    <div class="toast rejection-notice" data-testid="rejection-notice" aria-live="polite">
      <span>{rejections[rejections.length - 1]!.reason}</span>
      <button class="action" type="button" onclick={() => (rejections = [])}><Icon node={X} />Dismiss</button>
    </div>
  {/if}
{/if}

<!--
  IDEAS.md #8: the debug log is the third button-reached view state, same
  rules as browse/graveyard (no hotkeys, keyboard layer dead, authors
  nothing). It reads the RELAY, not the projection, so the transport rides
  in instead of the store.
-->
{#if view === 'debuglog' && store.transport}
  <span data-testid="debuglog-view-echo" hidden>{view}</span>
  <DebugLogView
    relay={store.transport}
    onBack={backToNotebook}
    resolveTaskTitle={(id) => projection.tasks[id]?.title ?? null}
    notebookTag={projection.notebook.tag}
    onSeed={seedTasks}
  />
{/if}

<!--
  The legal footer rides under every view state — the last in-flow content,
  before the modals (which must stay the last siblings, D8). It carries the
  stage's own `inert` expression — takeover, creation modal, and the settings
  delete modal own the page while they are up — so focus cannot reach the
  links from under an open surface.
-->
<LegalFooter
  inert={takeoverTaskId !== null || createModalOpen || (view === 'settings' && deleteTarget !== null)}
/>

<!--
  D8: the creation modal mounts as the LAST sibling — above the stage, the hub,
  and even a focus takeover (a capture to an unknown tag can open it there,
  Task 6). Whatever is behind stays present but not interactive: the stage and
  the hub carry `inert` while it is up.
-->
{#if createModalOpen}
  <NotebookCreateModal
    mode={editingNotebook ? 'edit' : 'create'}
    initialTag={capturedTag}
    initialFirstTask={capturedFirstTask}
    notebook={editingNotebook
      ? {
          id: editingNotebook.notebook_id,
          owner_id,
          name: editingNotebook.name,
          tag: editingNotebook.tag,
          description: editingNotebook.description,
          page_capacity: editingNotebook.page_capacity,
          timezone: 'UTC',
        }
      : null}
    onCommit={(draft: NotebookDraft) => {
      const id = editingNotebook?.notebook_id;
      if (id) void commitEdit(id, draft);
      else void commitCreate(draft);
    }}
    onCancel={cancelModal}
  />
{/if}

<!--
  R5.5: the account-deletion confirm modal — the last sibling, above every
  view it can be opened from (the account page and the settings sign-out
  footer), both of which carry `inert` while it is up. The deletion and the
  sign-out that follows are the container's (`commitDeleteAccount`).
-->
{#if deleteAccountOpen && signedIn}
  <DeleteAccountModal
    account={signedIn}
    {notebooks}
    ownerId={owner_id}
    onConfirm={() => void commitDeleteAccount()}
    onCancel={cancelDeleteAccount}
  />
{/if}

<style>
  /* R19: the same bar shape as the rejection notice (paper.css) — bottom-centre
     is the toast's own place, and the notice must not sit under the task the
     user is reading. Scoped here because this notice belongs to the auth
     surface, not to the outbox's verdicts. The bar's `.action` treatment is
     NOT here: paper.css owns it for all three notice bars, and a copy in this
     scoped block tied the dark override on specificity and lost to source
     order, leaving it dead. */
  .auth-expired-notice {
    display: flex;
    align-items: center;
    gap: 10px;
  }
</style>
